import { ToLogin } from "./ToLogin";
import { FC } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { StateType } from "../backend/store";
import { canAccessAny, hasPermission } from "../utils/permissions";
import { RedirectToLanding } from "./redirectToLanding";

type Props = {
  children: any;
  permission: string | string[];
  requireAll?: boolean;
  /**
   * Whole-branch review fix (CRITICAL 1), 2026-09-08.
   *
   * Scoped-resource pages (Contacts, Saved Routes) are NOT reachable on the
   * strength of holding a permission name: a `limited` user holds no global
   * permission at all, and reaches them through `scoped_user_permissions`
   * rows naming specific routes/households. `src/components/base/SideBar.tsx`
   * has always drawn that distinction when deciding whether to render an
   * entry -- `canAccessAny(user, permission, scope)` for scoped entries,
   * `hasPermission` for scope-less ones -- but the router had no gate at all,
   * so the distinction never existed here.
   *
   * Passing `scopeType` makes this component ask the sidebar's exact
   * question, and that equivalence is the whole point: it is what makes gating
   * these routes provably non-regressive. Anyone who is SHOWN the sidebar entry
   * passes the gate, so no menu item can ever be drawn for a user who is then
   * bounced off the page it links to. `/saved-routes` is the sharp case -- a
   * `limited` user holding route ids passes `canAccessAny(user, "read_routes",
   * "routes")` (which is what both the sidebar entry AND getDefaultLandingPage
   * use) while failing a plain `hasPermission("read_routes")`, so a gate built
   * on the latter would have taken the page away from him.
   *
   * Stage 1B-1 Task 10: a blocked user goes to his own landing page
   * (RedirectToLanding), no longer to "/" -- the public marketing page.
   *
   * Only meaningful with a single permission string, which is all
   * `canAccessAny` accepts.
   */
  scopeType?: "routes" | "households";
};

export const PermissionRoute: FC<Props> = ({
  children,
  permission,
  requireAll = false,
  scopeType
}) => {
  const isUser = localStorage.getItem("user");
  const currentUser = useSelector((state: StateType) => state.authStore.currentUser);

  // First check authentication
  if (!isUser) {
    return <ToLogin />
  }

  const allowed = scopeType && typeof permission === "string"
    ? canAccessAny(currentUser, permission, scopeType)
    : hasPermission(permission, currentUser, requireAll);

  // Then check permissions -- only once the user has loaded, so a permitted
  // user is not bounced during a reload (the server is the first gate).
  if (currentUser && !allowed) {
    return <RedirectToLanding user={currentUser} />;
  }

  return children;
};
